<template>
  <div class="fin-panel-body">
    <finance-state v-if="status === 'loading'" kind="loading" :lines="4" />
    <finance-state v-else-if="status === 'error'" kind="error" icon="mdi-alert-circle-outline" :title="$t('finance_shell.error_title')" :text="$t('finance_statement_lines_load_error')">
      <button type="button" class="ui-btn ui-btn--dark" @click="load"><v-icon size="16">mdi-refresh</v-icon>{{ $t("finance_shell.retry") }}</button>
    </finance-state>
    <finance-state v-else-if="!lines.length" kind="empty" icon="mdi-tray-full" :title="$t('finance_no_statement_lines')" />
    <template v-else>
      <div v-for="line in lines" :key="line.id" class="fin-line">
        <time>{{ shortDate(line.date) }}</time>
        <span class="fin-line__main">
          <button v-if="line.orderId" type="button" class="ui-link" @click="openOrder(line.orderId)">#{{ line.orderId }}</button>
          <small>{{ detail(line) }}</small>
        </span>
        <b class="fin-num" :class="line.amount < 0 ? 'is-out' : 'is-in'">{{ money(line.amount, true) }}</b>
      </div>
      <div class="fin-line fin-line--total">
        <span></span>
        <span>{{ $t("finance_total") }}</span>
        <b class="fin-num" :class="total < 0 ? 'is-out' : 'is-in'">{{ money(total, true) }}</b>
      </div>
    </template>
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import { formatMoney } from "@/components/restaurant/finance/constants";
import { formatShortDate } from "@/components/report/shared/periods";
import FinanceState from "../parts/FinanceState.vue";
import OrderPanel from "./OrderPanel.vue";
import { mapStatementLine, r2 } from "../financeLogic";

const RATE_SUFFIX = { 1: "/mj", 2: "%", 3: "/obrok", 4: "" };

/**
 * Redovi iza jednog broja razrade (GET statement/lines?category&from&to, data.lines): datum, narudžba, osnovica i stopa,
 * iznos sa predznakom iz direction. Zbir se računa iz redova (backend total je apsolutan).
 */
export default {
  name: "FinanceLinesPanel",
  components: { FinanceState },
  inject: { shell: "reportShell" },
  props: {
    restaurantId: { type: [Number, String], default: null },
    category: { type: String, default: "" },
    from: { type: String, default: "" },
    to: { type: String, default: "" },
    currency: { type: String, default: "KM" },
  },
  data: () => ({ status: "loading", lines: [] }),
  computed: {
    total() {
      return r2(this.lines.reduce((sum, line) => sum + line.amount, 0));
    },
  },
  created() {
    this.load();
  },
  methods: {
    money(value, signed) {
      return formatMoney(value, this.currency, { signed });
    },
    shortDate(value) {
      return value ? formatShortDate(String(value).slice(0, 10)) : "";
    },
    detail(line) {
      const rate =
        line.rateValue === null
          ? ""
          : Number(line.rateType) === 2
          ? `${line.rateValue} %`
          : `${formatMoney(line.rateValue, this.currency)}${RATE_SUFFIX[line.rateType] || ""}`;
      const base = line.baseAmount !== null && rate ? `${formatMoney(line.baseAmount, this.currency)} × ${rate}` : rate;
      return [this.translate(line.label), base].filter(Boolean).join(" · ");
    },
    async load() {
      if (!this.restaurantId || !this.category) {
        return;
      }
      this.status = "loading";
      try {
        const { data } = await restaurantFinanceService.getStatementLines(this.restaurantId, { category: this.category, from: this.from, to: this.to });
        const payload = (data && data.data) || data || {};
        const rows = Array.isArray(payload) ? payload : payload.lines || [];
        this.lines = rows.map(mapStatementLine);
        this.status = "ready";
      } catch (error) {
        this.status = "error";
      }
    },
    openOrder(orderId) {
      this.shell.openPanel({
        key: `order-${orderId}`,
        title: this.$t("finance_shell.order_title", { id: orderId }),
        component: OrderPanel,
        props: { orderId: Number(orderId), restaurantId: this.restaurantId, currency: this.currency },
      });
    },
  },
};
</script>
